<script setup lang="ts">
import { ref, onMounted } from "vue";
import { GoogleTag } from "@/utils/auth";
import { VInput } from "vuetify/components";


const code = ref<string | undefined>(undefined);
const params = ref<any | undefined>(undefined);
const testEventCode = ref<string | undefined>(undefined);
const show = ref(false);

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// 本地包装方法，避免模板直接引用 window 对象
const handleClose = () => { show.value = false; };
const handleTrigger = async (c: string) => {
  const codeStr = String(c);
  const amount = 50;
  const isKwai = sessionStorage.getItem("channel_id") === "9";
  const kwaiFlag = isKwai ? { channel_kind: "9" } : {};
  const mockOrderId = `TEST-ORDER-${Date.now()}`;

  const mockParams = (() => {
    switch (codeStr) {
      case "101":
        return { is_new: localStorage.getItem("isNew") === "true", ...kwaiFlag };
      case "102":
        return { ...kwaiFlag };
      case "103":
        return { amount, currency: "BRL", order_id: mockOrderId, ...kwaiFlag };
      case "104":
        return {
          recharge_amount: amount,
          currency: "BRL",
          value: amount,
          content_type: "product",
          content_ids: ["deposit_package"],
          num_items: 1,
          order_id: mockOrderId,
          ...kwaiFlag,
        };
      case "105":
        return { ...kwaiFlag };
      case "106":
        return { ...kwaiFlag };
      case "107":
        return { ...kwaiFlag };
      case "108":
        // 首充事件：按系统规则上报 104，并附加 108
        return {
          currency: "BRL",
          value: amount,
          content_type: "product",
          content_ids: ["first_deposit_package"],
          num_items: 1,
          first_recharge_count: 1,
          recharge_amount: amount,
          order_id: mockOrderId,
          additional_events: ["108"],
          ...kwaiFlag,
        };
      case "100":
      default:
        return { ...kwaiFlag };
    }
  })();

  console.log(`[EventActivator] trigger ${codeStr} with params:`, mockParams);
  if (codeStr === "108") {
    // 108 首充事件按系统规则作为 104 上报，并附加 108
    await GoogleTag("104", { ...mockParams, is_fb_test: true, test_event_code: testEventCode.value });
  } else {
    await GoogleTag(codeStr, { ...mockParams, is_fb_test: true, test_event_code: testEventCode.value });
  }
};

// 控制台工具：打开弹窗与触发事件（100-108），首充合并（104+108=附加事件）
const attachConsole = () => {
  (window as any).EventActivator = {
    // 打开激活弹窗（不带任何任务参数）
    open: (c?: string) => {
      code.value = c || "100";
      params.value = undefined;
      show.value = true;
      console.log(`[EventActivator] open modal. current code: ${code.value}`);
    },
    // 关闭弹窗
    close: () => {
      show.value = false;
    },
    // 触发事件：仅调用 GoogleTag，并根据事件码模拟参数
    trigger: async (c: string | number, p?: any) => {
      const codeStr = String(c);
      const amount = 50;
      const isKwai = sessionStorage.getItem("channel_id") === "9";
      const kwaiFlag = isKwai ? { channel_kind: "9" } : {};
      const mockOrderId = `TEST-ORDER-${Date.now()}`;

      const mockParams = (() => {
        switch (codeStr) {
          case "101":
            return { is_new: localStorage.getItem("isNew") === "true", ...kwaiFlag };
          case "102":
            return { ...kwaiFlag };
          case "103":
            return { amount, currency: "BRL", order_id: mockOrderId, ...kwaiFlag };
          case "104":
            return {
              recharge_amount: amount,
              currency: "BRL",
              value: amount,
              content_type: "product",
              content_ids: ["deposit_package"],
              num_items: 1,
              order_id: mockOrderId,
              ...kwaiFlag,
            };
          case "105":
            return { ...kwaiFlag };
          case "106":
            return { ...kwaiFlag };
          case "107":
            return { ...kwaiFlag };
          case "108":
            return { currency: "BRL", value: amount, order_id: mockOrderId, ...kwaiFlag };
          case "100":
          default:
            return { ...kwaiFlag };
        }
      })();

      const finalParams = p ?? mockParams;
      // console.log(`[EventActivator] trigger ${codeStr} with params:`, finalParams);
      await GoogleTag(codeStr, finalParams);
    },
  };
};

onMounted(() => {
  attachConsole();
});
</script>

<template>
  <div v-if="show" class="ea-overlay" @click.self="handleClose()">
    <div class="ea-modal">
      <div class="ea-header">
        <div class="ea-title">Event Activator</div>
        <button class="ea-close" @click="handleClose()">×</button>
      </div>
      <div class="ea-body">
        <div class="ea-desc">打开后不带参数，点击下方按钮手动触发事件。</div>
        <div class="ea-current">当前事件代码：{{ code || '-' }}</div>
        <div class="ea-grid">
          <v-text-field v-model="testEventCode" placeholder="输入自定义参数（JSON格式）" />
        </div>
        <div class="ea-grid">
          <button class="ea-btn" @click="handleTrigger('100')">100 PageView</button>
          <button class="ea-btn" @click="handleTrigger('101')">101 Visit</button>
          <button class="ea-btn" @click="handleTrigger('102')">102 CompleteRegistration</button>
          <button class="ea-btn" @click="handleTrigger('103')">103 InitiatedCheckout</button>
          <button class="ea-btn" @click="handleTrigger('104')">104 Purchase</button>
          <button class="ea-btn" @click="handleTrigger('105')">105 InviteReward</button>
          <button class="ea-btn" @click="handleTrigger('106')">106 Withdraw</button>
          <button class="ea-btn" @click="handleTrigger('107')">107 PhoneBind</button>
          <button class="ea-btn" @click="handleTrigger('108')">108 FirstPurchaseSuccess</button>
        </div>
      </div>
    </div>
  </div>
  
</template>

<style scoped>
/* overlay */
.ea-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.ea-modal {
  width: 720px;
  max-width: 92vw;
  background: #16181a;
  color: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.35);
  overflow: hidden;
}

.ea-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.ea-title { font-size: 16px; font-weight: 600; }
.ea-close {
  width: 32px; height: 32px;
  border: none; border-radius: 8px; cursor: pointer;
  background: rgba(255,255,255,0.08); color: #fff; font-size: 18px; line-height: 1;
}

.ea-body { padding: 16px; }
.ea-desc { font-size: 13px; color: rgba(255,255,255,0.65); margin-bottom: 8px; }
.ea-current { font-size: 13px; margin-bottom: 12px; }

.ea-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.ea-actions { display: flex; gap: 8px; margin-top: 12px; }

.ea-btn {
  padding: 10px 12px;
  background: #2b2f33;
  color: #fff;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  cursor: pointer;
}

.ea-btn:hover {
  background: #34383d;
}

.ea-btn.secondary {
  background: #1f2225;
}
</style>